import {
	useMutation,
	UseMutationResult,
	useQuery,
	useQueryClient,
	UseQueryResult,
} from '@tanstack/react-query';
import { useStore } from '../hooks/useStore';
import { V2 } from '@icecreamsocial/api-resources';
import { Campaign, queryKeys } from '../../queries/campaigns';

type UseCampaignArgs = {
	campaignId?: string;
}

type UseCampaignReturn = {
	campaign: UseQueryResult<Campaign>;
	update: UseMutationResult<Campaign>;
	create: UseMutationResult<Campaign>;
};

type UseCampaignFn = (args?: UseCampaignArgs) => UseCampaignReturn;

/**
 * Makes the campaign data and loading state available
 */
export const useCampaign: UseCampaignFn = ({
	campaignId,
} = {}) => {
	const { Orchestrators, __displayFlowSuccess } = useStore();
	const headers = { Authorization: Orchestrators.Auth.authHeader };
	const queryClient = useQueryClient();

	const campaign = useQuery<Campaign>(
		queryKeys.getCampaign({ campaignId }),
		async () => {
			const { data } = await V2.Campaigns.getCampaignById(
				{ campaignId },
				{},
				headers
			);
			return data?.data?.campaign;
		},
		{ enabled: !!campaignId, refetchOnWindowFocus: false }
	);

	const update = useMutation<Campaign, Error, Partial<Campaign>>({
		mutationFn: makeUpdateCampaign({ campaignId, headers }),
		onSuccess: (data: Campaign) => {
			queryClient.invalidateQueries(queryKeys.getCampaigns._def);
			queryClient.setQueryData(queryKeys.getCampaign({ campaignId }), data);
			/**
			 * @todo eliminate store dependency
			 */
			__displayFlowSuccess('Campaign settings updated!');
		},
	});

	const create = useMutation<Campaign, Error, Partial<Campaign>>({
		mutationFn: async (values: Partial<Campaign>) => {
			const formData = new FormData();
			// we need to append images last
			const {
				offerImage,
				thankYouImage,
				shareImage,
				useSMSConsent,
				useEmailConsent,
				...textValues
			} = values;
			Object.keys(textValues).forEach((key) => {
				if (!textValues[key]) return;

				formData.set(key, textValues[key]);
			});
			// handle the consent fields separately because the fact that they're booleans doesn't let the logic above
			//  work correctly for "turning on" a field
			if (useSMSConsent !== undefined) {
				formData.set('useSMSConsent', `${useSMSConsent}`);
			}
			if (useEmailConsent !== undefined) {
				formData.set('useEmailConsent', `${useEmailConsent}`);
			}
			if (offerImage) {
				formData.set('offerImage', offerImage);
			}
			if (thankYouImage) {
				formData.set('thankYouImage', thankYouImage);
			}
			if (shareImage) {
				formData.set('shareImage', shareImage);
			}

			const { data } = await V2.Campaigns.createCampaign({ formData }, {}, headers);

			console.debug('created', data?.data?.campaign);

			return data?.data?.campaign;
		},
		onSuccess: (data) => {
			queryClient.setQueryData(queryKeys.getCampaign._def, data);
			console.debug('campaign', data);
		}
	})

	return {
		campaign,
		update,
		create,
	};
};

function makeUpdateCampaign({ campaignId, headers }) {
	return async (values) => {
		const formData = new FormData();
		// we need to append images last
		const { offerImage, thankYouImage, shareImage, ...textValues } = values;

		Object.keys(textValues).forEach((key) => {
			// don't add null values because api doesn't know how to handle them
			if (
				(textValues[key] === undefined || textValues[key] === null) &&
				textValues[key] !== ''
			)
				return;

			formData.set(key, textValues[key]);
		});

		// set images only if they are files.  if they are strings, they are most likely the previous uri
		// this is so that we don't overwrite the image on subsequent saves
		if (typeof offerImage?.name === 'string') {
			formData.set('offerImage', offerImage);
		}
		if (typeof thankYouImage?.name === 'string') {
			formData.set('thankYouImage', thankYouImage);
		}
		if (typeof shareImage?.name === 'string') {
			formData.set('shareImage', shareImage);
		}

		const { data } = await V2.Campaigns.updateCampaign(
			{
				campaignId,
				formData,
			},
			{},
			headers
		);

		return data?.data?.campaign;
	};
}
